import { getRawInitData } from "../telegram/webApp"

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? ""

let sessionToken: string | null = null

export function getSessionToken(): string | null {
	if (!sessionToken) sessionToken = localStorage.getItem("ludo_session_token")
	return sessionToken
}

function setSessionToken(token: string): void {
	sessionToken = token
	localStorage.setItem("ludo_session_token", token)
}

/** The signed-in player's own user id, set once `authenticate()` succeeds.
 * Lets game screens tell which pieces/turns belong to "me" without asking
 * the player to pick themselves manually. */
export function getCurrentUserId(): string | null {
	return localStorage.getItem("ludo_user_id")
}

function setCurrentUserId(userId: string): void {
	localStorage.setItem("ludo_user_id", userId)
}

export interface ApiUser {
	id: string
	username: string | null
	first_name: string
	games_played: number
	games_won: number
	games_lost: number
	rating: number
}

export class ApiError extends Error {
	code: string
	constructor(code: string, message: string) {
		super(message)
		this.code = code
	}
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
	const headers = new Headers(options.headers)
	headers.set("content-type", "application/json")
	const token = getSessionToken()
	if (token) headers.set("authorization", `Bearer ${token}`)

	const res = await fetch(`${API_BASE}${path}`, { ...options, headers })
	const body = await res.json().catch(() => ({}))
	if (!res.ok) {
		const err = body?.error ?? { code: "UNKNOWN", message: "request failed" }
		throw new ApiError(err.code, err.message)
	}
	return body as T
}

/** Authenticates with the backend using verified Telegram initData. Must be
 * called once on app startup before any other authenticated API call. */
export async function authenticate(): Promise<{ sessionToken: string; user: ApiUser }> {
	const initData = getRawInitData()
	const result = await request<{ sessionToken: string; user: ApiUser }>("/api/auth/telegram", {
		method: "POST",
		body: JSON.stringify({ initData }),
	})
	setSessionToken(result.sessionToken)
	setCurrentUserId(result.user.id)
	return result
}

export function createRoom() {
	return request<{ roomCode: string; inviteLink: string; expiresAt: string }>("/api/rooms", { method: "POST" })
}

export function getRoom(roomCode: string) {
	return request<{ roomCode: string; hostUserId: string; status: string; maxPlayers: number; state: unknown }>(`/api/rooms/${roomCode}`)
}

export function joinRoom(roomCode: string) {
	return request<{ roomCode: string; color: string; seatIndex: number }>(`/api/rooms/${roomCode}/join`, { method: "POST" })
}

export function startRoom(roomCode: string) {
	return request<{ ok: true }>(`/api/rooms/${roomCode}/start`, { method: "POST" })
}

export function getLeaderboard() {
	return request<{ leaderboard: Array<{ rank: number; id: string; username: string | null; first_name: string; rating: number; games_played: number; games_won: number; games_lost: number }> }>("/api/leaderboard")
}

export function getProfile() {
	return request<{ user: ApiUser; recentGames: unknown[] }>("/api/users/me")
}

export function websocketUrl(roomCode: string): string {
	const token = getSessionToken() ?? ""
	const base = API_BASE.replace(/^http/, "ws") || (location.origin.replace(/^http/, "ws"))
	return `${base}/api/rooms/${roomCode}/websocket?token=${encodeURIComponent(token)}`
}
